import { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { MatchState, Team, Rally } from '@/types';
import { exportMatchReport, exportTournamentData } from '@/lib/export';
import { getTournamentData, getPlayerOfMatch, PlayerOfMatchWinner, getTournament, getParticipatingTeams } from '@/lib/supabase';
import AppHeader from '@/components/AppHeader';

interface MatchCompleteProps {
  matchState: MatchState;
  teams: Team[];
  onNewMatch: () => void;
  onSignOut: () => void;
  readOnly?: boolean;
}

export default function MatchComplete({ matchState, teams, onNewMatch, onSignOut, readOnly = false }: MatchCompleteProps) {
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [exporting, setExporting] = useState<'match' | 'tournament' | null>(null);
  const [playerOfMatch, setPlayerOfMatch] = useState<PlayerOfMatchWinner[]>([]);
  const [tournamentName, setTournamentName] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(!readOnly);

  // Load tournament name if match belongs to a tournament
  useEffect(() => {
    const tid = matchState.match?.tournamentId;
    if (!tid) { setTournamentName(null); return; }
    getTournament(tid)
      .then(t => setTournamentName(t.name))
      .catch(() => setTournamentName(null));
  }, [matchState.match?.tournamentId]);

  useEffect(() => {
    const matchId = matchState.match?.id;
    const status = matchState.match?.status;
    if (!matchId || status !== 'match-won') return;
    getPlayerOfMatch(matchId)
      .then(winners => { setPlayerOfMatch(winners); })
      .catch(err => console.error('[MatchComplete] getPlayerOfMatch failed:', err));
  }, [matchState.match?.id, matchState.match?.status]);

  // Fire confetti for scorer when match completes
  useEffect(() => {
    if (readOnly) return;
    const winnerColor = matchState.match?.winnerId === matchState.teamA?.id
      ? matchState.teamA?.color
      : matchState.teamB?.color;
    const colors = winnerColor ? [winnerColor, '#ffffff', '#FFD700'] : ['#2563EB', '#ffffff', '#FFD700'];

    // First burst
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.55 }, colors });
    // Side cannons for extra effect
    const t1 = setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors }), 250);
    const t2 = setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors }), 400);
    const t3 = setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { y: 0.4 }, colors, scalar: 0.8 }), 700);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportMatch = async () => {
    setExporting('match');
    try {
      await exportMatchReport(matchState, teams);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  const handleExportTournament = async () => {
    setExporting('tournament');
    try {
      const tournamentId = matchState.match?.tournamentId;
      const [data, participatingTeams] = await Promise.all([
        getTournamentData(tournamentId ?? undefined),
        tournamentId ? getParticipatingTeams(tournamentId) : Promise.resolve([]),
      ]);
      exportTournamentData(data, teams, tournamentId ?? undefined, participatingTeams);
    } catch (err) {
      console.error('Tournament export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  if (!matchState.match || !matchState.teamA || !matchState.teamB) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  const winner = matchState.match.winnerId === matchState.teamA.id ? matchState.teamA : matchState.teamB;
  const matchTypeLabel = matchState.match.matchType.charAt(0).toUpperCase() + matchState.match.matchType.slice(1);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with tournament context */}
      <AppHeader
        onSignOut={onSignOut}
        title={tournamentName || `Match #${matchState.match.matchNumber}`}
        subtitle={tournamentName ? `${matchTypeLabel} Match #${matchState.match.matchNumber}` : matchState.match.date}
        tournamentId={matchState.match.tournamentId ?? undefined}
        tournamentName={tournamentName ?? undefined}
      />

      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-md mx-auto w-full">
        {/* Thank you banner — scorer only */}
        {showThankYou && (
          <div className="relative mb-5 rounded-xl border border-border bg-surface px-4 py-3 flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-text-primary">Match complete — great job!</p>
              <p className="text-xs text-text-muted">Thanks for scoring. Your work keeps the game running.</p>
            </div>
            <button
              onClick={() => setShowThankYou(false)}
              className="flex-shrink-0 p-1 rounded text-text-muted hover:text-text-primary transition-colors"
              aria-label="Dismiss"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        )}

        {/* Match Winner */}
        <div
          className="rounded-xl p-8 mb-5 text-center"
          style={{ backgroundColor: `${winner.color}15`, borderColor: winner.color, borderWidth: 2 }}
        >
          <p className="text-sm text-text-muted uppercase mb-2">MATCH WON</p>

          {/* Team Logo - large and prominent */}
          <div className="flex justify-center mb-4">
            {winner.logoUrl ? (
              <img
                src={winner.logoUrl}
                alt={`${winner.shortName} logo`}
                className="w-[100px] h-[100px] rounded-xl object-contain bg-surface p-2"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) {
                    (fallback as HTMLElement).style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              className={`w-[100px] h-[100px] rounded-xl flex items-center justify-center ${winner.logoUrl ? 'hidden' : ''}`}
              style={{ backgroundColor: winner.color }}
            >
              <span className="text-white text-4xl font-bold">
                {winner.shortName?.charAt(0) || '?'}
              </span>
            </div>
          </div>

          <p className="text-2xl font-bold mb-1" style={{ color: winner.color }}>
            {winner.name.toUpperCase()}
          </p>
          <p className="text-lg text-text-secondary">{winner.shortName}</p>
        </div>

        {/* Player Of The Match */}
        {playerOfMatch.length > 0 ? (
          <div className="card mb-5 border-2 border-warning/30 bg-warning/5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏆</span>
              <h2 className="text-xs font-bold text-warning uppercase tracking-wider">Player Of The Match</h2>
            </div>
            <div className="space-y-3">
              {playerOfMatch.map(p => (
                <div key={p.playerId} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-text-primary">{p.playerName}</p>
                    <p className="text-xs text-text-muted">{p.teamName}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {'⭐'.repeat(Math.min(p.totalStars, 1))}
                      {p.totalStars > 10 && <span className="text-xs text-warning font-bold">×{p.totalStars}</span>}
                    </div>
                    <p className="text-[10px] text-text-muted mt-0.5">
                      {[
                        p.acerStars > 0 && `${p.acerStars}A`,
                        p.defenderStars > 0 && `${p.defenderStars}D`,
                        p.liberoStars > 0 && `${p.liberoStars}L`,
                        p.setterStars > 0 && `${p.setterStars}S`,
                        p.blockerStars > 0 && `${p.blockerStars}B`,
                        p.finisherStars > 0 && `${p.finisherStars}F`,
                      ].filter(Boolean).join(' ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : matchState.match?.status === 'match-won' && (
          <div className="card mb-5 border border-border">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🏆</span>
              <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">Player Of The Match</h2>
            </div>
            <p className="text-xs text-text-muted text-center py-2">No stars recorded yet. Score with volleyball categories to calculate.</p>
          </div>
        )}

        {/* Set Scores */}
        <div className="card mb-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">SET SCORES</h2>
          <div className="space-y-3">
            {matchState.sets.map((set, index) => (
              <div key={set.id} className="flex items-center justify-between py-2">
                <span className="text-sm text-text-secondary">Set {index + 1}</span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold w-6" style={{ color: matchState.teamA?.color }}>{matchState.teamA?.shortName}</span>
                    <span className="font-mono-nums text-xl font-bold text-text-primary">{set.scoreA}</span>
                  </div>
                  <span className="text-text-muted">—</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-nums text-xl font-bold text-text-primary">{set.scoreB}</span>
                    <span className="text-sm font-semibold w-6" style={{ color: matchState.teamB?.color }}>{matchState.teamB?.shortName}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Match Stats */}
        <div className="card mb-5">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">MATCH STATS</h2>
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Sets Played</span>
              <span className="font-mono-nums font-bold text-text-primary">{matchState.sets.length}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-text-secondary">Sets Won by {matchState.teamA.shortName}</span>
              <span className="font-mono-nums font-bold" style={{ color: matchState.teamA.color }}>{matchState.teamA.setsWon}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-text-secondary">Sets Won by {matchState.teamB.shortName}</span>
              <span className="font-mono-nums font-bold" style={{ color: matchState.teamB.color }}>{matchState.teamB.setsWon}</span>
            </div>
          </div>
        </div>

        {/* View Point History */}
        <button onClick={() => setShowFullHistory(!showFullHistory)} className="btn-secondary mb-4">
          {showFullHistory ? 'HIDE POINT HISTORY' : 'VIEW POINT HISTORY'}
        </button>

        {showFullHistory && (
          <div className="card mb-6 max-h-80 overflow-y-auto">
            {(() => {
              // Collect all rallies from all sets with running score
              const allRallyRows: {
                rallyNumber: number;
                setNumber: number;
                rally: Rally;
                scoreAfter: string;
              }[] = [];

              let globalRallyNumber = 0;
              for (let setIndex = 0; setIndex < matchState.sets.length; setIndex++) {
                const set = matchState.sets[setIndex];
                const rallies = set.rallies ?? [];
                // Rallies are stored descending (newest first) — reverse for chronological display
                const chronological = [...rallies].reverse();
                let runningScoreA = 0;
                let runningScoreB = 0;
                for (const rally of chronological) {
                  globalRallyNumber++;
                  if (rally.pointWonById === matchState.teamA?.id) runningScoreA++;
                  else if (rally.pointWonById === matchState.teamB?.id) runningScoreB++;
                  allRallyRows.push({
                    rallyNumber: globalRallyNumber,
                    setNumber: set.setNumber,
                    rally,
                    scoreAfter: `${runningScoreA}–${runningScoreB}`,
                  });
                }
              }

              if (allRallyRows.length === 0) {
                return (
                  <div className="text-center py-8">
                    <p className="text-text-muted text-sm">No rally history available.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-1">
                  {allRallyRows.map(({ rallyNumber, setNumber, rally, scoreAfter }) => {
                    const servingTeam = rally.servingTeamId === matchState.teamA?.id ? matchState.teamA : matchState.teamB;
                    const winningTeam = rally.pointWonById === matchState.teamA?.id ? matchState.teamA : matchState.teamB;
                    const server = servingTeam?.players.find(p => p.id === rally.serverId);
                    const allPlayers = [...(matchState.teamA?.players ?? []), ...(matchState.teamB?.players ?? [])];
                    const pointWinner = rally.pointWinnerId ? allPlayers.find(p => p.id === rally.pointWinnerId) : null;
                    return (
                      <div key={rally.id} className="flex items-center gap-2 px-3 py-2 text-xs border-b border-border last:border-b-0">
                        <span className="text-text-muted w-8">R{rallyNumber}</span>
                        <span className="text-text-muted w-6">S{setNumber}</span>
                        <span style={{ color: servingTeam?.color }} className="font-semibold w-8">{servingTeam?.shortName}</span>
                        <span style={{ color: servingTeam?.color }} className="font-semibold w-16 truncate">{server?.name}</span>
                        <span className="text-text-muted">→</span>
                        <span style={{ color: winningTeam?.color }} className="font-semibold w-8">{winningTeam?.shortName}</span>
                        <span className="text-text-muted uppercase w-8">{rally.lostReason}</span>
                        {pointWinner && (
                          <span style={{ color: winningTeam?.color }} className="font-semibold w-16 truncate">{pointWinner.name}</span>
                        )}
                        <span className="text-text-muted ml-auto">{scoreAfter}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Export Buttons */}
        <div className="space-y-3 mb-6">
          <button onClick={handleExportMatch} disabled={exporting !== null} className="btn-primary bg-team-a text-white font-bold py-4 flex items-center justify-center gap-2 disabled:opacity-50">
            {exporting === 'match' ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />EXPORTING...</>
            ) : 'EXPORT MATCH REPORT'}
          </button>
          <button onClick={handleExportTournament} disabled={exporting !== null} className="btn-primary bg-team-b text-white font-bold py-4 flex items-center justify-center gap-2 disabled:opacity-50">
            {exporting === 'tournament' ? (
              <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />EXPORTING...</>
            ) : 'EXPORT TOURNAMENT DATA'}
          </button>
        </div>

        {!readOnly && (
          <button onClick={onNewMatch} className="btn-primary bg-team-a text-white font-bold py-4">NEW MATCH</button>
        )}
      </div>
    </div>
  );
}
